import React, {
    useEffect, useLayoutEffect, useMemo, useState,
} from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { SingleValue } from 'react-select';

import classnames from 'classnames/bind';

import { notify } from '@NOTIFICATION/Notificator';

import {
    AdminSingleSectionDataVariant,
    AdminSingleSectionDataVariantVersion, AdminSingleSectionData, AdminSingleSectionMeta,
} from '@INTERFACES/admin-api/sections';

import { useUpdateVariant } from '@SERVICES';

import usePublishVariantModal from '@HOOKS/store/modals/usePublishVariantModal';
import useDocumentPermissions from '@HOOKS/useDocumentPermissions';

import BasicSelect from 'src/view/COMPONENTS/COMMON/inputs/select/inputs/BasicSelect';
import VersionInfo from '@COMPONENTS/SHARED/VersionInfo';
import Warning from '@COMPONENTS/SHARED/Warning';
import SaveAndPublishPairButtons from '@COMPONENTS/COMMON/buttons/SaveAndPublishPairButtons';

import styles from './VariantBodySidebar.module.scss';

const cx: CX = classnames.bind(styles);

function getOption(options: any[]) {
    const len = options.length;

    if (len > 0) {
        return options[len - 1];
    }

    return null;
}

function VariantBodySidebar(props: Props) {
    const {
        variant,
        sectionData,
        sectionMeta,
        selectedVersion,
        contentIsEmpty,
        hasChanges,
        getEditorContent,
        onVersionChange,
    } = props;

    const { versions } = variant;

    const {
        canEditDocument,
        canPublishDocument,
    } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    const {
        actions: publishVariantModalActions,
    } = usePublishVariantModal();

    const [
        updateVariant,
        {
            isLoading, isSuccess, isError, error,
        },
    ] = useUpdateVariant();

    const options = useMemo(() => {
        const opts = versions
            .map((version, i) => ({
                value: version.id,
                label: `Version ${i + 1}`,
            }));

        if (versions.length === 0 || variant.body !== versions[versions.length - 1].body) {
            opts.push({
                value: 0,
                label: 'Draft',
            });
        }

        return opts;
    }, [variant, versions]);

    const [selectedOption, setSelectedOption] = useState<any>(getOption(options));

    useLayoutEffect(() => {
        setSelectedOption(getOption(options));
    }, [variant, versions, options]);

    useLayoutEffect(() => {
        onVersionChange(selectedOption);
    }, [selectedOption, onVersionChange]);

    useEffect(() => {
        if (isSuccess) {
            notify.info('Variant body has been saved', {
                toastId: 'save-variant-body',
            });
        }
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            if ((error as FetchBaseQueryError).status === 403) {
                notify.error('Permission denied', {
                    toastId: 'save-variant-body-permission-error',
                });
            } else {
                notify.error('Something wrong!', {
                    toastId: 'save-variant-body-error',
                });
            }
        }
    }, [isError, error]);

    function getWarning() {
        if (variant.versions.length === 0) {
            return 'Variant is not published';
        }

        return 'Variant has unpublished changes';
    }

    const includedToVersionControl = !!selectedVersion?.description;



    return (
        <div className={cx('variant-body-sidebar')}>
            <div className={cx('sidebar-wrapper')}>
                <div className={cx('version-control')}>
                    <div className={cx('title')}>
                        Version Control
                    </div>
                    <div className={cx('versions')}>
                        <BasicSelect
                            title="Variant history"
                            isClearable={false}
                            menuPlacement="bottom"
                            options={options}
                            value={selectedOption}
                            onChange={(val: SingleValue<any>) => {
                                setSelectedOption(val);
                            }}
                        />
                    </div>

                    {
                        selectedVersion
                        && selectedVersion.author
                        && (
                            <VersionInfo
                                includedToVersionControl={includedToVersionControl}
                                version={{
                                    description: selectedVersion.description || '',
                                    createdAt: selectedVersion.createdAt,
                                    author: {
                                        fullName: selectedVersion.author.fullName,
                                        email: selectedVersion.author.email,
                                    },
                                }}
                            />
                        )
                    }
                </div>

                <div className={cx('footer')}>
                    {
                        !variant.isPublished
                        && (
                            <div className={cx('footer-top')}>
                                <Warning text={getWarning()} />
                            </div>
                        )
                    }

                    <SaveAndPublishPairButtons
                        isSaveProcessing={isLoading}
                        disablePublish={contentIsEmpty}
                        disableSave={!hasChanges}
                        canEditDocument={canEditDocument}
                        canPublishDocument={canPublishDocument}
                        onSaveClick={() => {
                            const content = getEditorContent();

                            updateVariant({
                                documentId: sectionData.document.id,
                                chapterId: sectionData.chapter.id,
                                sectionId: sectionData.id,
                                id: variant.id,
                                name: variant.name || '',
                                isVisible: variant.isVisible,
                                isDefault: variant.isDefault,
                                body: content,
                                brands: variant.brands,
                                regions: variant.regions,
                                areas: variant.areas,
                                countries: variant.countries,
                                propertyTypes: variant.propertyTypes,
                            });
                        }}
                        onSaveAndPublishClick={() => {
                            publishVariantModalActions.open({
                                id: variant.id,
                                body: getEditorContent(),
                                documentId: sectionData.document.id,
                                chapterId: sectionData.chapter.id,
                                sectionId: sectionData.id,
                            });
                        }}
                    />
                </div>
            </div>
        </div>
    );
}

VariantBodySidebar.propTypes = {
    contentIsEmpty: PropTypes.bool.isRequired,
    hasChanges: PropTypes.bool.isRequired,
    getEditorContent: PropTypes.func.isRequired,
    onVersionChange: PropTypes.func.isRequired,
};

type Props = InferProps<typeof VariantBodySidebar.propTypes> & {
    variant: AdminSingleSectionDataVariant,
    sectionData: AdminSingleSectionData,
    sectionMeta: AdminSingleSectionMeta,
    selectedVersion: SingleValue<AdminSingleSectionDataVariantVersion | undefined>,
};

export default VariantBodySidebar;
